import React, { useState, useRef } from 'react';
import { GeneralDialog } from 'sharedComponents/GeneralDialog';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { switchToAnotherGatewayRequest, validateSwitchGatewayAssetRequest, getGatewayAssetsListRequest, clearValidateSwitchGatewayAssetState, resetSwitchGatewayValidationResult } from './actions';
import { SearchBox } from 'sharedComponents';
import PropTypes from 'prop-types';
import { IconCheckedFilled, IconError } from 'svgIcons/MotionPortalIcons';
import { LoadingStatusWrapper } from 'sharedComponents/WithLoadingStatus';
import colors from 'theme/_colors.scss';
import './style.scss';
import { apiCallStatus } from 'helpers/constants';
const MAX_SEARCH_LENGTH = 50;
const SwitchToAnotherGatewayComponent = ({ show, close, asset, gatewayID, gatewaySerialNumber, isSwitchValid, validateSwitchLoadingStatus, gatewayAssetsList, gatewayAssetsListLoadingStatus, isSwitchEgwToAnotherGateway, switchGatewayId, onResetAssetInfo }) => {
    const { t: translate } = useTranslation();
    const dispatch = useDispatch();
    const assetList = gatewayAssetsList?.gatewayAssets || [];
    const gatewayAssetsListError = gatewayAssetsList?.errorMessage || null;
    const validateSwitchGatewayMessage = useSelector(({ powertrainTools }) => powertrainTools?.validateSwitchGatewayAsset?.validationMessage);
    const [selectedAssetId, setSelectedAssetId] = useState(null);
    const [serialNumber, setSerialNumber] = useState(gatewaySerialNumber || null);
    const handleValidate = async () => {
        dispatch(validateSwitchGatewayAssetRequest(asset?.assetId, switchGatewayId, selectedAssetId, isSwitchEgwToAnotherGateway));
    };
    const handleMove = () => {
        dispatch(switchToAnotherGatewayRequest(asset?.assetId, switchGatewayId, selectedAssetId, isSwitchEgwToAnotherGateway, gatewayID, onResetAssetInfo));
        close();
        dispatch(clearValidateSwitchGatewayAssetState());
    };
    const handleClose = () => {
        dispatch(clearValidateSwitchGatewayAssetState());
        close();
    };

    const debounceTimeout = useRef();
    const handleChangeSearch = (value) => {
        if (value?.length <= MAX_SEARCH_LENGTH) {
            setSerialNumber(value);
            setSelectedAssetId(null);
            dispatch(clearValidateSwitchGatewayAssetState());
            if (debounceTimeout?.current) {
                clearTimeout(debounceTimeout?.current);
            }
            if (value) {
                debounceTimeout.current = setTimeout(() => {
                    dispatch(getGatewayAssetsListRequest(value));
                }, 350);
            }
        }
    };
    const handleSelectAsset = (assetId) => {
        setSelectedAssetId(assetId);
        dispatch(resetSwitchGatewayValidationResult());
    };
    return (
        <GeneralDialog
            id='switch_asset_dialog'
            show={show}
            close={handleClose}
            dialogClassName='switch_asset_dialog'
            closeButton={true}
            title={<h1 className='switch_asset_dialog__title'>
                <span className='switch_asset_dialog__title-text'>
                    {translate('ABB.Powertrain.Frontend.SwitchToAnotherGateway')}
                </span>
            </h1>}
            cancelButtonProps={{
                text: translate('ABB.Powertrain.Frontend.cancelLabel'),
            }}
            acceptButtonProps={{
                text: isSwitchValid
                    ? translate('ABB.Powertrain.Frontend.SwitchLabel')
                    : translate('ABB.Powertrain.Frontend.ValidateLabel'),
                onClick: isSwitchValid ? handleMove : handleValidate,
                disabled: validateSwitchLoadingStatus === apiCallStatus.LOADING || !serialNumber || !selectedAssetId,
            }}
            persistent={false}
        >
            <div className='switch__asset__input-container'>
                {isSwitchValid === true &&
                    <div className='switch__asset__success-container'>
                        <IconCheckedFilled
                            width={18}
                            height={18}
                            color={colors.jungleGreen}
                            className='success-icon'
                        />
                        <span>{translate('ABB.Powertrain.Frontend.ValidationSuccess')}</span>
                    </div>
                }
                {isSwitchValid === false &&
                    <div className='switch__asset__error-container'>
                        <IconError
                            width={18}
                            height={18}
                            color={colors.pomegranate}
                            className='error-icon'
                        />
                        <span>{validateSwitchGatewayMessage}</span>
                    </div>
                }
                {serialNumber && gatewayAssetsListError &&
                    <div className='switch__asset__error-container'>
                        <IconError
                            width={18}
                            height={18}
                            color={colors.pomegranate}
                            className='error-icon'
                        />
                        <span>{gatewayAssetsListError}</span>
                    </div>
                }
                <LoadingStatusWrapper
                    loadingStatus={[validateSwitchLoadingStatus, gatewayAssetsListLoadingStatus]}
                    transparent
                    useCustomAnchor>

                    <div id='switch__asset__label'>
                        {translate('ABB.Powertrain.Frontend.TargetGatewaySerialNumber')}
                    </div>
                    <div className='switch__asset__input-wrapper'>
                        <div className='switch__asset__input-wrapper__inner'>
                            <SearchBox
                                className='search-box'
                                placeholder={translate('ABB.Powertrain.Frontend.gatewayManagementSearch')}
                                onChange={handleChangeSearch}
                                value={serialNumber}
                                iconDisabled={!serialNumber}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && serialNumber) {
                                        e.preventDefault();
                                        dispatch(getGatewayAssetsListRequest(serialNumber));
                                    }
                                }}
                            />
                        </div>
                        <div id='switch__asset__message'>
                            {translate('ABB.Powertrain.Frontend.SwitchGatewayDescription')}
                        </div>
                        <div className='switch__asset__asset-list-heading'>
                            {translate('ABB.Powertrain.Frontend.selectDrive')}
                        </div>
                        {assetList?.length > 0 && serialNumber ?
                            <div className='switch__asset__asset-list' id='switch__asset__asset-list_group'>
                                {assetList.map(asset =>
                                    <button
                                        key={asset.assetId}
                                        type='button'
                                        id={`switch_asset_list_item_${selectedAssetId}`}
                                        className={`switch__asset__asset-item${selectedAssetId === asset?.assetId ? ' selected' : ''}`}
                                        onClick={() => handleSelectAsset(asset?.assetId)}
                                        aria-pressed={selectedAssetId === asset?.assetId}
                                    >
                                        <span className='drive-name__text' title={asset?.name}>{asset?.name}</span>
                                        <span className='drive-name__slno' title={asset?.serialNumber}>{asset?.serialNumber}</span>
                                    </button>
                                )}
                            </div>
                            :
                            <div className='switch__asset__asset-list-empty' id='switch__asset__asset-list_empty_data'>
                                {translate('ABB.Powertrain.Frontend.NoGatewayDetected')}
                            </div>
                        }
                    </div>

                </LoadingStatusWrapper>
            </div>
        </GeneralDialog >
    );
};
SwitchToAnotherGatewayComponent.propTypes = {
    show: PropTypes.bool,
    close: PropTypes.func,
    gatewayID: PropTypes.any,
    gatewaySerialNumber: PropTypes.string,
    asset: PropTypes.object,
    isMoveValid: PropTypes.bool,
    validationLoadingStatus: PropTypes.string,
    gatewayAssetsList: PropTypes.any,
    gatewayAssetsListLoadingStatus: PropTypes.string,
    isSwitchEgwToAnotherGateway: PropTypes.bool,
    onResetAssetInfo: PropTypes.func
};
export default SwitchToAnotherGatewayComponent;
